import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, FormInput, Button } from './index';
import SectionPanel from './SectionPanel';
import { WidgetActionRow } from './WidgetActionRow';
import { userPreferencesApi } from '../services/api';
import { useCustomerInvite } from '../hooks/useCustomerInvite';

interface CustomerInviteWidgetProps {
  className?: string;
  collapsible?: boolean;
  /** Increment to force-expand the widget, focus the name field and briefly highlight it. */
  focusToken?: number;
  /** Render inside the shared dark-blue SectionPanel banner ("Bjud in kund")
   * instead of the widget's own in-body heading. */
  banner?: boolean;
}

/**
 * The compact invite form on the contractor's start page. The page
 * Bjud in beställare (/customers/invite) has its own layout (869fj44u8).
 */
export function CustomerInviteWidget({ className, collapsible = false, focusToken, banner = false }: CustomerInviteWidgetProps) {
  const { t } = useTranslation();
  const { form: inviteForm, errors: inviteErrors, isInviting, result, error, handleChange: handleInviteChange, handleSubmit: handleInviteSubmit } = useCustomerInvite();
  const [urlCopied, setUrlCopied] = useState(false);
  const invitationUrl = result?.registrationUrl ?? null;
  // Collapsible support (used on the company start page). First-paint hint in
  // localStorage avoids an open→collapse flash; the per-user server preference
  // is authoritative so the choice follows the logged-in person.
  const [collapsed, setCollapsed] = useState(
    () => collapsible && localStorage.getItem('inviteWidgetCollapsedHint') === 'true'
  );
  const [collapseAnimate, setCollapseAnimate] = useState(false);
  const [highlight, setHighlight] = useState(false);

  // When the guide's "Bjud in kund" action fires, expand the widget, focus the
  // name field and briefly highlight it so the user sees where they landed.
  useEffect(() => {
    if (!focusToken) return;
    setCollapseAnimate(true);
    setCollapsed(false);
    setHighlight(true);
    const focusTimer = setTimeout(() => {
      document.getElementById('invite-customer-name')?.focus();
    }, 150);
    const highlightTimer = setTimeout(() => setHighlight(false), 2000);
    return () => {
      clearTimeout(focusTimer);
      clearTimeout(highlightTimer);
    };
  }, [focusToken]);

  const setCollapsedByUser = (c: boolean) => {
    setCollapseAnimate(true);
    setCollapsed(c);
    localStorage.setItem('inviteWidgetCollapsedHint', String(c));
    userPreferencesApi.savePreference('inviteWidgetCollapsed', c).catch(() => {});
  };

  useEffect(() => {
    if (!collapsible) return;
    userPreferencesApi.getPreferences().then(prefs => {
      if (typeof prefs.inviteWidgetCollapsed === 'boolean') {
        setCollapsed(prefs.inviteWidgetCollapsed);
        localStorage.setItem('inviteWidgetCollapsedHint', String(prefs.inviteWidgetCollapsed));
      }
    }).catch(() => {});
  }, [collapsible]);

  const handleCopyUrl = () => {
    if (invitationUrl) {
      navigator.clipboard.writeText(invitationUrl).then(() => {
        setUrlCopied(true);
        setTimeout(() => setUrlCopied(false), 2000);
      });
    }
  };

  const cardClassName = `${className ?? ''} ${highlight ? 'border-primary border-2 shadow' : ''}`.trim();

  // In the narrow banner column each field gets its own row; otherwise three
  // fields per row on md+.
  const fieldColClass = banner ? 'col-12' : 'col-12 col-md-4';

  const toggleButton = collapsible ? (
    <button
      type="button"
      className="btn btn-sm btn-light-primary flex-shrink-0"
      onClick={() => setCollapsedByUser(!collapsed)}
      aria-expanded={!collapsed}
      aria-label={collapsed ? t('common.expand', 'Visa mer') : t('common.collapse', 'Minimera')}
      title={collapsed ? t('common.expand', 'Visa mer') : t('common.collapse', 'Minimera')}
    >
      <i className={`feather-chevron-${collapsed ? 'down' : 'up'}`}></i>
    </button>
  ) : null;

  const collapsibleContent = (
      <div
        style={collapsible ? {
          maxHeight: collapsed ? 0 : 2000,
          opacity: collapsed ? 0 : 1,
          overflow: 'hidden',
          transition: collapseAnimate ? 'max-height 2s ease, opacity 2s ease' : 'none'
        } : undefined}
      >
      {!collapsible && (
        <h5 className="fw-bold mb-3">
          <i className="feather-user-plus me-2"></i>
          {t('customerInvite.formTitle')}
        </h5>
      )}

      {result && <div className="alert alert-success mb-3">{result.message}</div>}
      {error && <div className="alert alert-danger mb-3">{error}</div>}

      {invitationUrl && (
        <div className="mb-3">
          <label className="form-label fw-bold">{t('customerInvite.urlLabel')}</label>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              value={invitationUrl}
              readOnly
            />
            <button
              className="btn btn-outline-primary"
              type="button"
              onClick={handleCopyUrl}
            >
              <i className={`feather-${urlCopied ? 'check' : 'copy'} me-1`}></i>
              {urlCopied ? t('customerInvite.urlCopied') : t('common.copy', 'Kopiera')}
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleInviteSubmit} noValidate className="invite-widget-dense">
        <div className="row g-2 align-items-start">
          <div className={fieldColClass}>
            <FormInput
              id="invite-customer-name"
              name="customerName"
              label={t('customerInvite.customerName')}
              value={inviteForm.customerName}
              onChange={handleInviteChange}
              placeholder={t('customerInvite.customerNamePlaceholder')}
              error={inviteErrors.customerName ? t(inviteErrors.customerName) : undefined}
              required
            />
          </div>
          <div className={fieldColClass}>
            <FormInput
              name="customerEmail"
              type="email"
              label={t('customerInvite.customerEmail')}
              value={inviteForm.customerEmail}
              onChange={handleInviteChange}
              error={inviteErrors.customerEmail ? t(inviteErrors.customerEmail) : undefined}
              required
            />
          </div>
          <div className={fieldColClass}>
            <FormInput
              type="tel"
              name="customerPhone"
              label={t('customerInvite.customerPhone')}
              value={inviteForm.customerPhone}
              onChange={handleInviteChange}
              placeholder={t('customerInvite.customerPhonePlaceholder')}
              error={inviteErrors.customerPhone ? t(inviteErrors.customerPhone) : undefined}
            />
          </div>
        </div>
        {/* The same text for every invitation, never whether the address has an account (869fmx898). */}
        <p className="small text-muted mb-3" data-testid="invite-existing-account-hint">{t('customerInvite.existingAccountHint')}</p>
        <WidgetActionRow>
          <Button type="submit" isLoading={isInviting}>
            <i className="feather-send me-2"></i>
            {t('customerInvite.submit')}
          </Button>
        </WidgetActionRow>
      </form>
      </div>
  );

  if (banner) {
    return (
      <SectionPanel
        title={t('customerInvite.bannerTitle')}
        icon="user-plus"
        className={cardClassName || undefined}
        fillHeight={false}
        headerAction={toggleButton}
      >
        {collapsibleContent}
      </SectionPanel>
    );
  }

  return (
    <Card className={cardClassName}>
      {collapsible && (
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="fw-bold mb-0">
            <i className="feather-user-plus me-2"></i>
            {t('customerInvite.formTitle')}
          </h5>
          {toggleButton}
        </div>
      )}
      {collapsibleContent}
    </Card>
  );
}
